import React, { useId } from 'react';
import { ChoiceChip } from '../ds';
import { inputClass } from '../ds/Dialog';
import { INSTRUMENTS, CATEGORIES, FULL_MIX, OTHER } from '../../utils/instruments';

/**
 * Two-level instrument choice: a category, then its type. "Other" asks for a description and
 * "Full mix" explains its different score. `value` is { category, type, other }; `guessed` says the
 * current choice came from the file name.
 */
export const InstrumentPicker = ({ value, onChange, guessed = false }) => {
  const otherId = useId();
  const { category, type, other } = value;
  const types = INSTRUMENTS[category] || [];

  return (
    <fieldset className="m-0 p-0 border-0 flex flex-col gap-space-3 min-w-0">
      <legend className="p-0 mb-space-1 font-display text-project text-ink">What instrument is this?</legend>
      <span className="text-small text-muted">
        {guessed
          ? "We guessed from the filename. Change it if it's wrong. It helps the feedback fit the instrument."
          : 'It helps the feedback fit the instrument.'}
      </span>
      <div className="flex flex-wrap gap-space-2">
        {CATEGORIES.map((label) => (
          <ChoiceChip
            key={label}
            selected={label === category}
            onSelect={() => onChange({ category: label, type: INSTRUMENTS[label][0] || null, other: label === OTHER ? other : '' })}
          >
            {label}
          </ChoiceChip>
        ))}
      </div>
      {types.length > 0 && (
        <div className="flex flex-col gap-space-2 p-space-4 rounded-radius-md bg-base">
          <span className="text-label text-muted">{category}: which one?</span>
          <div className="flex flex-wrap gap-space-2">
            {types.map((label) => (
              <ChoiceChip key={label} selected={label === type} onSelect={() => onChange({ ...value, type: label })}>
                {label}
              </ChoiceChip>
            ))}
          </div>
        </div>
      )}
      {category === OTHER && (
        <div className="flex flex-col gap-space-2">
          <label htmlFor={otherId} className="text-label text-ink">Describe the instrument</label>
          <input
            id={otherId}
            type="text"
            maxLength={60}
            value={other || ''}
            onChange={(event) => onChange({ ...value, other: event.target.value })}
            placeholder="e.g. kalimba, field recording, vocal chop"
            className={inputClass}
          />
        </div>
      )}
      {category === FULL_MIX && (
        <p className="m-0 p-space-4 rounded-radius-md bg-base text-small text-ink-soft">
          A full mix gets a mastering readiness score instead of a stem health score.
        </p>
      )}
    </fieldset>
  );
};

/** The API's category and instrument_type for a picker value, or null if it isn't complete */
export const instrumentFields = ({ category, type, other }) => {
  if (!category) return null;
  if (category === OTHER) return other?.trim() ? { category, instrumentType: other.trim() } : null;
  return { category, instrumentType: type || null };
};

/** A picker value for a clip's saved tag */
export const pickerValue = ({ category, instrument_type: instrumentType }) => (
  category === OTHER
    ? { category, type: null, other: instrumentType || '' }
    : { category, type: instrumentType || null, other: '' }
);
